import React, { useState, useEffect } from "react";
import "../styles/home.css";
import "../styles/blogs.css";
import Footer from "../components/layout/footer"; // <-- Add this import

export default function BlogFour() {
  const [sticky, setSticky] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const root = document.getElementById("root");
    if (root) {
      root.classList.add("home-root");
      root.style.flexGrow = "1";
    }

    const handleScroll = () => {
      setSticky(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="home-page">
     
      {/* Header / Navbar */}
      <header className={`navbar ${sticky ? "sticky" : ""}`}>
        <div className="nav-container">
          <div className="nav-left">
            <a href="/" className="logo">Roam</a>
            <nav>
              <a href="/#features" className="fs-14 color-low">Features</a>
              <a href="/#resources" className="fs-14 color-low">Resources</a>
              <a href="/#faqs" className="fs-14 color-low">FAQs</a>
              <a href="/#pricing" className="fs-14 color-low">Pricing</a>
            </nav>
          </div>
          <div className="nav-right">
            <a href="/login" className="btn-transparent" target="_blank" rel="noreferrer">Login</a>
            <a href="/register" className="btn-white" target="_blank" rel="noreferrer">Sign Up</a>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <section className="blog-wrapper section-features">
        <div className="blog-container">
          <div className="blog-body-text">
            
            {/* Header Block */}
            <h1 className="blog-main-title">
              How Much Will You Make on a 13-Week Travel Nurse Contract?
            </h1>
            <p className="blog-lead-paragraph" style={{ fontStyle: 'italic', opacity: '0.80', fontWeight: '300' }}>
              A 13-week total is more useful than a weekly headline when it includes scheduled wages, stipends, overtime assumptions, taxes, housing, travel, and one-time expenses.
            </p>

            {/* Intro */}
            <h3 className="blog-section-title">Start with the contract, not the advertisement</h3>
            <p className="blog-paragraph">
              A weekly travel healthcare rate is easy to compare, but a 13-week assignment is a financial project. It has recurring income, recurring expenses, one-time costs, possible bonuses, and risks such as canceled hours. Calculating the full contract value helps answer a better question: "What is this assignment expected to add to my finances by the time it ends?"
            </p>

            {/* Step 1 */}
            <h3 className="blog-section-title">Step 1: Calculate regular taxable wages</h3>
            <p className="blog-paragraph">
              Multiply the taxable hourly wage by the guaranteed regular hours and the number of paid weeks.
            </p>
            <blockquote className="blog-blockquote" style={{ margin: '20px 0', padding: '15px 20px', borderLeft: '4px solid #4A5568', background: '#1A202C', borderRadius: '4px' }}>
              <strong style={{ color: '#A0AEC0', display: 'block', marginBottom: '5px', textTransform: 'uppercase', fontSize: '12px', tracking: '1px' }}>Formula</strong>
              <span style={{ fontSize: '16px', color: '#E2E8F0' }}>Taxable hourly rate × regular hours per week × paid weeks = regular taxable wages for the contract</span>
            </blockquote>
            <p className="blog-paragraph">
              Example: $30 per hour × 36 hours × 13 weeks = $14,040 in regular taxable wages.
            </p>

            {/* Step 2 */}
            <h3 className="blog-section-title">Step 2: Add housing and meal allowances</h3>
            <p className="blog-paragraph">
              Multiply each weekly allowance by the number of eligible weeks. Confirm whether allowances are reduced during orientation, missed shifts, facility cancellations, requested time off, or partial weeks.
            </p>
            <ul className="blog-list">
              <li>Housing allowance: $900 × 13 = $11,700.</li>
              <li>Meals and incidentals allowance: $450 × 13 = $5,850.</li>
              <li><strong>Total listed allowances:</strong> $17,550.</li>
            </ul>
            <p className="blog-paragraph">
              The tax treatment of allowances depends on the arrangement and the worker's circumstances. Keep them separate from taxable wages until the correct treatment is established.
            </p>

            {/* Step 3 */}
            <h3 className="blog-section-title">Step 3: Add realistic overtime and differentials</h3>
            <p className="blog-paragraph">
              Only include overtime that is reasonably expected. If overtime is optional or frequently canceled, create separate scenarios instead of putting the most optimistic number into the base contract total.
            </p>
            <p className="blog-paragraph">
              For example, assume one eight-hour overtime shift every other week, producing six overtime shifts during the 13-week contract. At an overtime rate of $45 per hour:
            </p>
            <blockquote className="blog-blockquote" style={{ margin: '20px 0', padding: '15px 20px', borderLeft: '4px solid #4A5568', background: '#1A202C', borderRadius: '4px' }}>
              <strong style={{ color: '#A0AEC0', display: 'block', marginBottom: '5px', textTransform: 'uppercase', fontSize: '12px', tracking: '1px' }}>Overtime Example</strong>
              <span style={{ fontSize: '16px', color: '#E2E8F0' }}>6 shifts × 8 hours × $45 = $2,160 in projected overtime wages</span>
            </blockquote>
            <p className="blog-paragraph">
              Shift differentials, holiday premiums, call-back pay, and bonuses should each be listed on their own line. This makes it easier to remove or adjust an assumption without rebuilding the entire estimate.
            </p>

            {/* Step 4 */}
            <h3 className="blog-section-title">Step 4: Add one-time reimbursements and bonuses</h3>
            <ul className="blog-list">
              <li>Travel reimbursement.</li>
              <li>License or certification reimbursement.</li>
              <li>Scrub or equipment allowance.</li>
              <li>Sign-on, completion, or extension bonus.</li>
              <li>Reimbursement for physicals, testing, or onboarding requirements.</li>
            </ul>
            <p className="blog-paragraph">
              Check the conditions. A completion bonus that disappears after a facility cancellation should not be treated as guaranteed. A reimbursement that requires receipts should not be counted unless the expense is expected and the documentation can be provided.
            </p>

            {/* Step 5 */}
            <h3 className="blog-section-title">Step 5: Estimate payroll taxes and deductions</h3>
            <p className="blog-paragraph">
              Estimate federal, state, and local withholding using the worker's actual profile and assignment location. Add employee benefit deductions, retirement contributions, garnishments, or other payroll items that are expected to reduce the paycheck.
            </p>
            <p className="blog-paragraph">
              Avoid using one flat percentage for every assignment when comparing different states or localities. The convenience is attractive, but so is choosing the wrong contract based on a fictional tax result.
            </p>

            {/* Step 6 */}
            <h3 className="blog-section-title">Step 6: Subtract the cost of completing the assignment</h3>
            <ul className="blog-list">
              <li>Temporary housing and utilities.</li>
              <li>Deposits or nonrefundable fees.</li>
              <li>Travel to and from the assignment.</li>
              <li>Rental car, commuting, tolls, fuel, and parking.</li>
              <li>Duplicate housing or household costs at the tax home.</li>
              <li>Unreimbursed licensing, testing, certifications, and uniforms.</li>
              <li>Insurance gaps or increased premiums.</li>
              <li>Planned unpaid time off.</li>
            </ul>

            {/* Breakdown Table */}
            <h3 className="blog-section-title">Complete 13-week example</h3>
            <div style={{ overflowX: 'auto', margin: '24px 0', border: '1px solid #2D3748', borderRadius: '6px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '400px' }}>
                <thead>
                  <tr style={{ background: '#1A202C', borderBottom: '1px solid #2D3748' }}>
                    <th style={{ padding: '12px 16px', color: '#A0AEC0', fontSize: '14px', fontWeight: '600' }}>Income or Cost Category</th>
                    <th style={{ padding: '12px 16px', color: '#A0AEC0', fontSize: '14px', fontWeight: '600', textAlign: 'right' }}>13-Week Amount</th>
                  </tr>
                </thead>
                <tbody style={{ color: '#E2E8F0', fontSize: '15px' }}>
                  <tr style={{ borderBottom: '1px solid #2D3748' }}>
                    <td style={{ padding: '12px 16px' }}>Regular taxable wages</td>
                    <td style={{ padding: '12px 16px', textAlign: 'right' }}>$14,040</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #2D3748' }}>
                    <td style={{ padding: '12px 16px' }}>Projected overtime wages</td>
                    <td style={{ padding: '12px 16px', textAlign: 'right' }}>$2,160</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #2D3748' }}>
                    <td style={{ padding: '12px 16px' }}>Housing allowance</td>
                    <td style={{ padding: '12px 16px', textAlign: 'right' }}>$11,700</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #2D3748' }}>
                    <td style={{ padding: '12px 16px' }}>Meals and incidentals allowance</td>
                    <td style={{ padding: '12px 16px', textAlign: 'right' }}>$5,850</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #2D3748' }}>
                    <td style={{ padding: '12px 16px' }}>Travel reimbursement</td>
                    <td style={{ padding: '12px 16px', textAlign: 'right' }}>$600</td>
                  </tr>
                  <tr style={{ borderBottom: '2px solid #4A5568' }}>
                    <td style={{ padding: '12px 16px' }}>Completion bonus</td>
                    <td style={{ padding: '12px 16px', textAlign: 'right' }}>$1,000</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #2D3748', background: '#2D3748' }}>
                    <td style={{ padding: '12px 16px', fontWeight: 'bold' }}>Gross listed contract value</td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 'bold' }}>$35,350</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #2D3748' }}>
                    <td style={{ padding: '12px 16px', color: '#FC8181' }}>Estimated payroll taxes and deductions</td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', color: '#FC8181' }}>-$4,900</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #2D3748' }}>
                    <td style={{ padding: '12px 16px', color: '#FC8181' }}>Temporary housing and utilities</td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', color: '#FC8181' }}>-$10,400</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #2D3748' }}>
                    <td style={{ padding: '12px 16px', color: '#FC8181' }}>Travel and local transportation</td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', color: '#FC8181' }}>-$1,600</td>
                  </tr>
                  <tr style={{ borderBottom: '2px solid #4A5568' }}>
                    <td style={{ padding: '12px 16px', color: '#FC8181' }}>Other assignment expenses</td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', color: '#FC8181' }}>-$850</td>
                  </tr>
                  <tr style={{ background: '#22543D' }}>
                    <td style={{ padding: '12px 16px', fontWeight: 'bold', color: '#C6F6D5' }}>Estimated spendable contract value</td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 'bold', color: '#C6F6D5' }}>$17,600</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="blog-paragraph">
              The estimated spendable value is calculated as $35,350 - $4,900 - $10,400 - $1,600 - $850 = $17,600. Divide by 13 weeks to get approximately $1,353.85 per week after the listed taxes, deductions, and assignment expenses.
            </p>
            <p className="blog-paragraph">
              This example is not a market-rate claim or a tax calculation for any particular worker. It demonstrates the structure of the calculation. Replace every figure with the actual contract terms and personal estimates.
            </p>

            {/* Scenarios Section */}
            <h3 className="blog-section-title">Run at least three scenarios</h3>
            <ul className="blog-list" style={{ listStyleType: 'none', paddingLeft: 0 }}>
              <li style={{ marginBottom: '16px' }}><strong style={{ color: '#E2E8F0', display: 'block' }}>Conservative scenario</strong> No overtime, expected unpaid cancellations, higher housing estimate, and no conditional bonus.</li>
              <li style={{ marginBottom: '16px' }}><strong style={{ color: '#E2E8F0', display: 'block' }}>Expected scenario</strong> Realistic scheduled hours, probable overtime, expected costs, and only reasonably likely bonuses.</li>
              <li style={{ marginBottom: '16px' }}><strong style={{ color: '#E2E8F0', display: 'block' }}>Optimistic scenario</strong> Available overtime, full bonuses, and lower-than-expected expenses.</li>
            </ul>
            <p className="blog-paragraph">
              A contract that remains worthwhile in the conservative scenario is more resilient. A contract that only works in the optimistic scenario may still be acceptable, but the decision should acknowledge the risk.
            </p>

            {/* Final Section */}
            <h3 className="blog-section-title">Compare the same output for every contract</h3>
            <p className="blog-paragraph">
              When two assignments use different pay structures, compare estimated spendable value, not just gross contract value. Roam Pays is built to turn location, hourly pay, overtime, stipends, meal and housing allowances, and personal tax information into a consistent contract comparison. Saved contracts can then be reviewed side by side instead of reconstructed every time a recruiter sends a new screenshot.
            </p>

            {/* Call To Action */}
            <hr className="blog-divider" />
            <p className="blog-paragraph" style={{ color: '#ffffff' }}>
              Calculate your next 13-week contract with Roam Pays. Compare estimated take-home pay and spendable value before choosing an offer.
            </p>

            {/* Footer Notes */}
            <hr className="blog-divider" />
            <p className="blog-disclaimer">
              Disclaimer: This article provides general educational information and is not tax, legal, accounting, financial, or employment advice. All examples are hypothetical. Use current contract terms and consult qualified professionals regarding individual circumstances.
            </p>
            
            <h5 className="blog-sources-title">Sources</h5>
            <ul className="blog-sources-list">
              <li>IRS Publication 463, Travel, Gift, and Car Expenses</li>
              <li>IRS Publication 15, Employer's Tax Guide</li>
              <li>GSA FY 2026 CONUS Per Diem Rates announcement</li>
              <li>U.S. Department of Labor Fact Sheet #54, Health Care Overtime</li>
              <li>Roam Pays App Store listing</li>
            </ul>

          </div>
        </div>
      </section>

      {/* Footer */}
     <Footer />
    </div>
  );
}